import { z } from 'zod';
import { Confidence } from './entities.js';
/**
 * Tools the retrieval agent can pick per sub-query.
 *
 *   - graph:   Cypher traversal of the entity-relationship graph (Neo4j).
 *              Best for "who is X connected to" / "what role did X play".
 *   - vector:  Cosine top-K against the document-embedding cache.
 *              Best for "what does the corpus say about X" / narrative
 *              questions that don't lift off a specific edge.
 *   - community: Leiden community summaries (issue #37; not wired yet).
 *              Reserved on the wire so the planner can already pick it
 *              and the orchestrator can no-op the call until #37 lands.
 */
export const AgentToolName = z.enum(['graph', 'vector', 'community']);
/**
 * Why the planner generated this sub-query. The orchestrator uses
 * `intent` to pick a default hop count when the planner doesn't supply
 * one (lookup → 1, connection → 2, narrative/context → 1 with vector).
 */
export const SubQueryIntent = z.enum([
    'lookup',
    'connection',
    'context',
    'narrative',
]);
/**
 * One unit of retrieval work. The planner emits 1–N of these per
 * iteration; the orchestrator routes each through the listed tools.
 *
 * `entities` are the entity names the sub-query operates on. The
 * planner is told to draw from the known-entity list — unmatched names
 * are dropped before the graph call.
 *
 * `hops` is only meaningful for graph-tool sub-queries; clamped 1–3 by
 * the orchestrator. Default behavior follows `intent`.
 */
export const SubQuery = z.object({
    id: z.string(),
    query: z.string().min(1),
    intent: SubQueryIntent,
    tools: z.array(AgentToolName).min(1),
    entities: z.array(z.string()).optional(),
    hops: z.number().int().min(1).max(3).optional(),
});
/**
 * Output of the planner LLM call.
 *
 *   - direct:     question is simple enough to skip decomposition; the
 *                 orchestrator runs the legacy single-shot path.
 *   - decomposed: run each `subqueries[i]` through its tools, then
 *                 evaluate.
 *
 * `reasoning` is one or two sentences for the SSE trace pane.
 */
export const RetrievalPlan = z.object({
    approach: z.enum(['direct', 'decomposed']),
    subqueries: z.array(SubQuery),
    reasoning: z.string().optional(),
});
/**
 * Output of the critic LLM call. The orchestrator loops while
 * `sufficient: false` AND `iteration < MAX_ITERATIONS`. `refinements`
 * is the planner's next-iteration sub-query list, generated alongside
 * the verdict so the loop doesn't need a second LLM call.
 *
 * `confidence` is the critic's self-rated 0–1 belief that the
 * accumulated evidence answers the question. UI surfaces it via the
 * existing `<ConfidenceMeter>` component.
 */
export const Critique = z.object({
    sufficient: z.boolean(),
    confidence: Confidence,
    gaps: z.array(z.string()).default([]),
    refinements: z.array(SubQuery).default([]),
});
/**
 * Hard ceiling for iterations. Each iteration is one
 * planner-or-critic pass + N sub-query executions, so the worst case
 * is roughly MAX_ITERATIONS × (1 critic call + 3 sub-query calls + 1
 * final synthesis) ≈ 13 LLM calls. The orchestrator's per-iteration
 * timeout caps wall-clock cost.
 */
export const AGENT_MAX_ITERATIONS = 4;
/**
 * Wall-clock timeout the orchestrator races each long-running step
 * against — the planner LLM call, the critic LLM call, and each
 * sub-query's tool execution (graph + vector retrieval). Each raced
 * step gets the full timeout independently; this is NOT a shared
 * per-iteration budget. On timeout the orchestrator substitutes a
 * fallback (a `direct` plan, a sufficient critique, or a skipped
 * sub-query) so the loop keeps making progress instead of hanging
 * on a stalled provider or DB call.
 */
export const AGENT_ITERATION_TIMEOUT_MS = 30_000;
